import { useSyncExternalStore } from 'react';

import {
  cloneContent,
  isPlainObject,
  mergeValue,
  replaceInPlace,
  toEditable,
  type JsonObject,
} from './merge';

/**
 * The live link between the marketing site's content modules and what the
 * platform owner published.
 *
 * Each content module registers its exports here as it loads
 * (`registerSiteContent('page.home', { HERO, FAQS })`). Registration keeps a
 * pristine copy of the shipped values and immediately lays the owner's edits
 * over the exported objects *in place* - the components keep importing the
 * same constants they always did and render the edited copy.
 *
 * Published edits arrive from `GET /meta/site-content` and are remembered in
 * localStorage, so a returning visitor's first paint is already the edited
 * site rather than a flash of the shipped copy. A preview tab opened from the
 * editor additionally lays the editor's unsaved draft over its section.
 */

type Sections = Record<string, JsonObject>;

interface Registered {
  live: Record<string, unknown>;
  defaults: Record<string, unknown>;
  /** The item a list that ships empty is edited with (see `registerSiteContent`). */
  templates: Record<string, object>;
}

const PUBLISHED_STORAGE_KEY = 'neoncarts.siteContent.v1';
const DRAFT_STORAGE_KEY = 'neoncarts.siteContent.draft';
const PREVIEW_SESSION_KEY = 'neoncarts.siteContent.preview';
/** Opening any marketing page with this query parameter starts preview mode. */
export const PREVIEW_QUERY_PARAM = 'site-preview';

const registry = new Map<string, Registered>();
const listeners = new Set<() => void>();
let version = 0;

function readJson(storage: Storage | undefined, key: string): unknown {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function writeJson(storage: Storage | undefined, key: string, value: unknown): void {
  try {
    if (value === null) storage?.removeItem(key);
    else storage?.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or a full quota: the site still works, it just refetches.
  }
}

const local = typeof window !== 'undefined' ? window.localStorage : undefined;
const session = typeof window !== 'undefined' ? window.sessionStorage : undefined;

function asSections(value: unknown): Sections {
  if (!isPlainObject(value)) return {};
  const out: Sections = {};
  for (const [key, content] of Object.entries(value)) {
    if (isPlainObject(content)) out[key] = content as JsonObject;
  }
  return out;
}

let published: Sections = asSections(readJson(local, PUBLISHED_STORAGE_KEY));

function startPreviewFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has(PREVIEW_QUERY_PARAM)) {
    try {
      session?.setItem(PREVIEW_SESSION_KEY, '1');
    } catch {
      // Without sessionStorage preview lasts for this page only.
      return true;
    }
  }
  try {
    return session?.getItem(PREVIEW_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

let previewing = startPreviewFromUrl();

interface Draft {
  key: string;
  content: JsonObject;
}

function readDraft(): Draft | null {
  const raw = readJson(local, DRAFT_STORAGE_KEY);
  if (!isPlainObject(raw) || typeof raw.key !== 'string' || !isPlainObject(raw.content))
    return null;
  return { key: raw.key, content: raw.content as JsonObject };
}

let draft: Draft | null = previewing ? readDraft() : null;

function overrideFor(key: string): JsonObject | undefined {
  if (draft && draft.key === key) return draft.content;
  return published[key];
}

/** The shipped value, or a one-item list of its template if it ships empty. */
function baseFor(entry: Registered, name: string): unknown {
  const value = entry.defaults[name];
  const template = entry.templates[name];
  return Array.isArray(value) && value.length === 0 && template ? [template] : value;
}

function mergedFor(entry: Registered, name: string, override: JsonObject | undefined): unknown {
  const value = override?.[name];
  // With nothing edited a list that ships empty stays empty, template or not.
  if (value === undefined) return cloneContent(entry.defaults[name]);
  return mergeValue(baseFor(entry, name), value);
}

function apply(key: string, entry: Registered): void {
  const override = overrideFor(key);
  for (const [name, live] of Object.entries(entry.live)) {
    replaceInPlace(live, mergedFor(entry, name, override));
  }
}

function notify(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function applyAll(): void {
  for (const [key, entry] of registry) apply(key, entry);
  notify();
}

/**
 * Make a content module's exports editable as one section. Call at the bottom
 * of the module with the exported objects and arrays (not primitives - they
 * cannot be updated in place). ``templates`` gives the shape of an item for a
 * list that ships empty, which otherwise has no item to copy.
 */
export function registerSiteContent(
  key: string,
  exports: Record<string, object>,
  templates: Record<string, object> = {},
): void {
  if (registry.has(key)) return;
  const entry: Registered = { live: exports, defaults: {}, templates };
  for (const [name, value] of Object.entries(exports)) entry.defaults[name] = cloneContent(value);
  registry.set(key, entry);
  if (overrideFor(key)) apply(key, entry);
}

/** The shipped (never edited) content of a registered section, as JSON. */
export function siteContentDefaults(key: string): JsonObject | null {
  const entry = registry.get(key);
  if (!entry) return null;
  const out: JsonObject = {};
  for (const [name, value] of Object.entries(entry.defaults)) {
    const editable = toEditable(value);
    if (editable !== undefined) out[name] = editable;
  }
  return out;
}

/** The registered section with ``override`` laid over its defaults, as JSON. */
export function siteContentMerged(
  key: string,
  override: JsonObject | undefined,
): JsonObject | null {
  const entry = registry.get(key);
  if (!entry) return null;
  const out: JsonObject = {};
  for (const name of Object.keys(entry.defaults)) {
    const editable = toEditable(mergedFor(entry, name, override));
    if (editable !== undefined) out[name] = editable;
  }
  return out;
}

/** Item templates for the lists in a section that ship empty, as JSON. */
export function siteContentTemplates(key: string): JsonObject {
  const out: JsonObject = {};
  for (const [name, template] of Object.entries(registry.get(key)?.templates ?? {})) {
    const editable = toEditable(template);
    if (editable !== undefined) out[name] = editable;
  }
  return out;
}

/** What `GET /meta/site-content` returned: apply it and remember it. */
export function setPublishedSiteContent(sections: Record<string, unknown>): void {
  const next = asSections(sections);
  if (JSON.stringify(next) === JSON.stringify(published)) return;
  published = next;
  writeJson(local, PUBLISHED_STORAGE_KEY, Object.keys(next).length > 0 ? next : null);
  applyAll();
}

/** The editor's unsaved section, shown by any preview tab. Null clears it. */
export function setSiteContentDraft(key: string | null, content?: JsonObject): void {
  writeJson(local, DRAFT_STORAGE_KEY, key && content ? { key, content } : null);
}

export function isSiteContentPreview(): boolean {
  return previewing;
}

export function exitSiteContentPreview(): void {
  try {
    session?.removeItem(PREVIEW_SESSION_KEY);
  } catch {
    // Nothing to clear.
  }
  previewing = false;
  draft = null;
  applyAll();
}

if (typeof window !== 'undefined') {
  // The editor writes the draft in another tab; a preview tab follows along.
  window.addEventListener('storage', (event) => {
    if (event.key === DRAFT_STORAGE_KEY && previewing) {
      draft = readDraft();
      applyAll();
    }
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Changes whenever edited content is applied. Key the marketing tree on it so
 * components that read the content constants render again.
 */
export function useSiteContentVersion(): number {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => 0,
  );
}
